import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {BehaviorSubject, combineLatest, filter, map, of, shareReplay, startWith, Subject, Subscription, switchMap} from 'rxjs';
import { MessageService } from '@shared/services/message-service';

import {getSiteInfo} from '@shared/helpers/site-info';
import {ShiftStatus} from '@shared/models/worked-shift';
import {CustomValidators} from '@shared/helpers/custom-validators';
import {saveDebounce} from '@shared/helpers/save-debounce';

import {InvCountTpmInfo} from '../../../../models/inv-count-tpm-info';
import {InventoryService} from '../../../../services/inventory.service';
import {ShiftContextService} from '../../../../services/shift-context.service';
import { WeeklyAllocationsService } from '@shared/services/weekly-allocations.service';
import { fromFormControl } from '@shared/helpers/from-form-control';

@Component({
    selector: 'app-tpm-trailer-summary',
    templateUrl: './tpm-trailer-summary.component.html',
    styleUrls: ['./tpm-trailer-summary.component.scss']
})
export class TpmTrailerSummaryComponent implements OnInit, OnDestroy {

    invCountTpmInfo: InvCountTpmInfo;

    showShift: number = 0; // 1-5, 0 for all

    tpmForm = new FormGroup({
        walmartTrailersInYard: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        startOfDayRpcLoads: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        startOfDayUnsortedRpcPallets: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        breakPackTrailers: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        unprocessedBoxTrailers: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        plRoadworthyTrailers: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        comments: new FormControl<string>(''),
        emptyWalmartTrailers: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        goodWoodWalmartTrailers: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        goodWoodInDcDoors: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        goodWoodInRacks: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        automationTrailersOnYard: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        automationTrailersInDoors: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        rpcTrailersReadyToShip: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        rpcToscaTrailersEmpty: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        rpcIfcoTrailersEmpty: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        toscaMeatTrailers: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        toscaEggTrailers: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0) ]),
        storeReturnsWorkedS1: new FormControl<number>(0, [ Validators.required, CustomValidators.numeric(false), Validators.min(0), Validators.max(75)]),
        storeReturnsWorkedS2: new FormControl<number>(0,  [ Validators.required, CustomValidators.numeric(false), Validators.min(0), Validators.max(75)]),
        storeReturnsWorkedS3: new FormControl<number>(0,  [ Validators.required, CustomValidators.numeric(false), Validators.min(0), Validators.max(75)]),
        storeReturnsWorkedS4: new FormControl<number>(0,  [ Validators.required, CustomValidators.numeric(false), Validators.min(0), Validators.max(75)]),
        storeReturnsWorkedS5: new FormControl<number>(0,  [ Validators.required, CustomValidators.numeric(false), Validators.min(0), Validators.max(75)]),
        uacClaimsTrailers: new FormControl<number>(0, [ CustomValidators.numeric(false), Validators.min(0) ]),
        plsOffSite: new FormControl<number>(0, [CustomValidators.numeric(false), Validators.min(0)]),
        gwOffSite: new FormControl<number>(0, [CustomValidators.numeric(false), Validators.min(0)]),
    });

    returnsTotal: number = 0;

    rackCapacity = new BehaviorSubject<number | null>(null);

    rackWarning = combineLatest([
        this.rackCapacity,
        fromFormControl(this.tpmForm.controls.goodWoodInRacks)
    ]).pipe(
        map(([rackCapacity, goodWoodInRacks]) => {
            console.log('rack warning', rackCapacity, goodWoodInRacks);
            if (rackCapacity && goodWoodInRacks && goodWoodInRacks > rackCapacity)
                return 'GW in Racks exceeds capacity of ' + rackCapacity;
            else
                return null;
        }));

    private subscriptions: Subscription[];
    private saveSubscription: Subscription | null = null;
    shutdown: Subject<void> = new Subject<void>();

    constructor(
        private inventoryService: InventoryService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService,
        private allocationService: WeeklyAllocationsService) {
    }

    ngOnInit() {
        this.subscriptions = [
            this.inventoryService.CurrentInvTpmInfo
                .pipe(filter(info => !!info))
                .subscribe(info => {
                    this.invCountTpmInfo = info;
                    if (info) {
                        this.saveSubscription?.unsubscribe();

                        console.log('setting form values');

                        this.tpmForm.setValue({
                            walmartTrailersInYard: info.walmartTrailersInYard,
                            startOfDayRpcLoads: info.startOfDayRpcLoads,
                            startOfDayUnsortedRpcPallets: info.startOfDayUnsortedRpcPallets,
                            breakPackTrailers: info.breakPackTrailers,
                            unprocessedBoxTrailers: info.unprocessedBoxTrailers,
                            plRoadworthyTrailers: info.plRoadworthyTrailers,
                            comments: info.comments ?? '',
                            emptyWalmartTrailers: info.emptyWalmartTrailers,
                            goodWoodWalmartTrailers: info.goodWoodWalmartTrailers,
                            goodWoodInDcDoors: info.goodWoodInDcDoors,
                            goodWoodInRacks: info.goodWoodInRacks,
                            automationTrailersOnYard: info.automationTrailersOnYard,
                            automationTrailersInDoors: info.automationTrailersInDoors,
                            rpcTrailersReadyToShip: info.rpcTrailersReadyToShip,
                            rpcToscaTrailersEmpty: info.rpcToscaTrailersEmpty,
                            rpcIfcoTrailersEmpty: info.rpcIfcoTrailersEmpty,
                            toscaMeatTrailers: info.toscaMeatTrailers,
                            toscaEggTrailers: info.toscaEggTrailers,
                            storeReturnsWorkedS1: info.storeReturnsWorkedS1,
                            storeReturnsWorkedS2: info.storeReturnsWorkedS2,
                            storeReturnsWorkedS3: info.storeReturnsWorkedS3,
                            storeReturnsWorkedS4: info.storeReturnsWorkedS4,
                            storeReturnsWorkedS5: info.storeReturnsWorkedS5,
                            uacClaimsTrailers: info.uacClaimsTrailers,
                            plsOffSite: info.plsOffSite,
                            gwOffSite: info.gwOffSite
                        }, {

                        });

                        this.saveSubscription = this.tpmForm.valueChanges
                            .pipe(saveDebounce(this.shutdown))
                            .subscribe(_ => {
                                this.saveTpmTrailerSummary();
                            });
                    }
                }),

            this.shiftContextService.CurrentContext.subscribe(context => {
                if (getSiteInfo(context.site).isTpm)
                    this.showShift = 0;
                else
                    this.showShift = context.shift.number;

                if (context.status !== ShiftStatus.Open)
                    this.tpmForm.disable({ emitEvent: false });
                else
                    this.tpmForm.enable({ emitEvent: false });
            }),

            this.tpmForm.controls.goodWoodInRacks.valueChanges.subscribe(val => {
                console.log('good wood in racks', val);
            }),

            combineLatest([
                this.shiftContextService.CurrentContext,
                this.inventoryService.CurrentSubinventoryId
            ]).pipe(switchMap(([context, subinventoryId]) => {
                console.log('getting rack capacity', context, subinventoryId);
                if (!context || !subinventoryId)
                    return of(null);

                return this.allocationService.GetMetricForDateAsync(subinventoryId, 35, context.date);
            })).subscribe(racksCapacityMetric => {
                console.log('rack capacity metric', racksCapacityMetric);
                this.rackCapacity.next(racksCapacityMetric ? +racksCapacityMetric.activityValue : null);
            }),

            combineLatest([
                fromFormControl(this.tpmForm.controls.storeReturnsWorkedS1),
                fromFormControl(this.tpmForm.controls.storeReturnsWorkedS2),
                fromFormControl(this.tpmForm.controls.storeReturnsWorkedS3),
                fromFormControl(this.tpmForm.controls.storeReturnsWorkedS4),
                fromFormControl(this.tpmForm.controls.storeReturnsWorkedS5)
            ]).subscribe(vals => {
                this.returnsTotal = vals.reduce((acc, val) => acc + val, 0);
            }),
        ];
    }

    ngOnDestroy() {
        this.shutdown.next();
        this.subscriptions?.forEach(s => s.unsubscribe());
        this.saveSubscription?.unsubscribe();
        this.saveSubscription = null;
    }

    getFormValues(): InvCountTpmInfo {
        const formData: InvCountTpmInfo = {
            ...this.invCountTpmInfo,
            ...this.tpmForm.value,
        };

        formData.comments ??= '';

        return formData;
    }

    private saveTpmTrailerSummary(): void {
        if (!this.tpmForm.valid) {
            this.tpmForm.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Update Trailer Summary',
                detail: 'Please fix the listed errors.'
            });
        } else {
            this.inventoryService.updateInvCountTpmInfo(this.getFormValues()).subscribe(_ => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Tpm Trailer Summary Updated',
                    detail: ''
                });
            });
        }
    }
}
